import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Play } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GlassCard, GradButton, Field, ConfirmDelete, fmtMoney } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import { TarihInput } from "@/components/TarihInput";
import { Th, BosDurum } from "@/components/ortak/Liste";
import api, { errMsg } from "@/lib/api";

const BOS = { cari_id: "", tutar: "", gun: 1, tekrar_sayisi: 12, baslangic: "", aciklama: "", aktif: true };

const MaasDialog = ({ ilk, personeller, kapat, kaydedildi }) => {
  const [f, setF] = useState(ilk);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const kaydet = async () => {
    try {
      const body = { ...f, tutar: Number(f.tutar), gun: Number(f.gun), tekrar_sayisi: Number(f.tekrar_sayisi), baslangic: f.baslangic || null };
      f.id ? await api.put(`/maas-talimatlari/${f.id}`, body) : await api.post("/maas-talimatlari", body);
      toast.success("Maaş talimatı kaydedildi"); kaydedildi();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && kapat()}>
      <DialogContent className="glass max-w-lg" data-testid="maas-dialog">
        <DialogHeader><DialogTitle className="font-head">{f.id ? "Maaş talimatını düzenle" : "Yeni tekrarlı maaş"}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Personel *" className="sm:col-span-2"><SelectBox testid="maas-personel" value={f.cari_id} onChange={(v) => set("cari_id", v)} options={personeller.map((p) => ({ value: p.id, label: `${p.unvan} · ${p.kod}` }))} placeholder="Personel seçin" /></Field>
          <Field label="Net maaş (TL) *"><Input data-testid="maas-tutar" type="number" step="any" className="bg-white/5" value={f.tutar} onChange={(e) => set("tutar", e.target.value)} /></Field>
          <Field label="Her ayın günü (1-28)"><Input data-testid="maas-gun" type="number" min="1" max="28" className="bg-white/5" value={f.gun} onChange={(e) => set("gun", e.target.value)} /></Field>
          <Field label="Tekrar sayısı (0 = süresiz)"><Input data-testid="maas-tekrar" type="number" min="0" className="bg-white/5" value={f.tekrar_sayisi} onChange={(e) => set("tekrar_sayisi", e.target.value)} /></Field>
          {!f.id && <Field label="Başlangıç tarihi"><TarihInput data-testid="maas-baslangic" value={f.baslangic} onChange={(e) => set("baslangic", e.target.value)} /></Field>}
          <Field label="Açıklama" className="sm:col-span-2"><Input data-testid="maas-aciklama" className="bg-white/5" value={f.aciklama} onChange={(e) => set("aciklama", e.target.value)} /></Field>
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="maas-aktif" checked={f.aktif} onCheckedChange={(v) => set("aktif", v)} />Aktif</label>
        </div>
        <p className="text-xs text-muted-foreground">Seçilen günde maaş, personelin carisine <b>borç</b> (biz borçluyuz) olarak otomatik yazılır. Ödemeyi personelin cari sayfasından "İşlem Ekle → Ödeme" ile kasa/bankadan yaparsınız. Geçmiş başlangıç tarihi verirseniz kaçırılan aylar da oluşur.</p>
        <div className="flex justify-end gap-2"><button onClick={kapat} className="px-5 py-2 rounded-full border border-white/10 text-sm hover:bg-white/5">Vazgeç</button><GradButton data-testid="maas-kaydet" onClick={kaydet}>Kaydet</GradButton></div>
      </DialogContent>
    </Dialog>
  );
};

export const TekrarliMaaslar = () => {
  const [list, setList] = useState(null);
  const [personeller, setPersoneller] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([api.get("/maas-talimatlari"), api.get("/cariler", { params: { kategori: "personel" } })]);
      setList(m.data); setPersoneller(p.data.filter((x) => !x.isten_cikis));
    } catch (e) { toast.error(errMsg(e)); setList([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const calistir = async () => { try { const { data } = await api.post("/maas-talimatlari/calistir"); toast.success(data.olusan ? `${data.olusan} maaş tahakkuku oluştu` : "Günü gelen maaş yok"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const sil = async (id) => { try { await api.delete(`/maas-talimatlari/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!list) return null;
  return (
    <div data-testid="tekrarli-maaslar">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="font-head text-xl font-semibold text-[var(--g1)]">Tekrarlı Maaşlar</h1>
        <div className="ml-auto flex gap-2">
          <button onClick={calistir} data-testid="maas-calistir" className="inline-flex items-center gap-1.5 px-3 h-10 rounded-lg text-xs border border-white/15 hover:bg-white/5"><Play className="w-4 h-4" />Günü gelenleri şimdi işle</button>
          <GradButton data-testid="maas-ekle" onClick={() => setEdit(BOS)}><Plus className="w-4 h-4" />Yeni Talimat</GradButton>
        </div>
      </div>
      <GlassCard className="p-4">
        {!list.length ? <BosDurum testid="maas-bos" text="Henüz tekrarlı maaş yok. Maaşlar her ay seçtiğiniz günde sabah 06:00'da otomatik işlenir." /> : (
          <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="maas-tablo">
            <thead><tr><Th>Cari Adı</Th><Th>Oluşturma Tarihi</Th><Th>Oluşturma Periyodu</Th><Th>Tekrar Sayısı</Th><Th>Sonraki</Th><Th right>Tutar</Th><Th className="w-20" /></tr></thead>
            <tbody>{list.map((m) => (
              <tr key={m.id} className={`border-t border-white/5 ${m.aktif ? "" : "opacity-45"}`} data-testid={`maas-satir-${m.id}`}>
                <td className="p-2.5 font-semibold text-xs uppercase">{m.cari_unvan}</td>
                <td className="p-2.5 text-xs text-center">{(m.created_at || "").slice(0, 10).split("-").reverse().join(".")}</td>
                <td className="p-2.5 text-xs text-center">Her Ayın Belirli Günü - {m.gun}</td>
                <td className="p-2.5 text-xs text-center" data-testid={`maas-tekrar-${m.id}`}>{m.olusan}/{m.tekrar_sayisi || "∞"}</td>
                <td className="p-2.5 text-xs text-center">{m.aktif ? m.sonraki_tarih.split("-").reverse().join(".") : "Tamamlandı / pasif"}</td>
                <td className="p-2.5 text-xs text-right font-mono">{fmtMoney(m.tutar)}</td>
                <td className="p-2.5 text-right whitespace-nowrap">
                  <button data-testid={`maas-duzenle-${m.id}`} onClick={() => setEdit(m)} className="p-2 rounded-lg text-muted-foreground hover:text-white hover:bg-white/10"><Pencil className="w-4 h-4" /></button>
                  <ConfirmDelete testid={`maas-sil-${m.id}`} onConfirm={() => sil(m.id)} />
                </td>
              </tr>))}</tbody>
          </table></div>
        )}
      </GlassCard>
      {edit && <MaasDialog ilk={edit} personeller={edit.id ? [...personeller, { id: edit.cari_id, unvan: edit.cari_unvan, kod: "" }].filter((p, i, a) => a.findIndex((x) => x.id === p.id) === i) : personeller}
        kapat={() => setEdit(null)} kaydedildi={() => { setEdit(null); load(); }} />}
    </div>
  );
};
